import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Bell, Check, ChevronDown, Trash2, BookOpen } from 'lucide-react';
import { detectAuthor } from '../data/reflectionAuthors';

/**
 * NotificationBell
 * Bell icon with unread badge. On open: fetches latest notifications,
 * shows them in a dropdown, lets the user mark items read.
 * Polls unread count every 30s.
 */
export const NotificationBell = ({ onNavigate }) => {
  const { API } = useContext(AuthContext);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // For read-content notifications (like daily reflection) that open in a modal
  const [readerNotif, setReaderNotif] = useState(null);
  const [bioOpen, setBioOpen] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnread(res.data.unread || 0);
    } catch (e) {
      console.error('NotificationBell: failed to fetch unread count', e);
    }
  }, [API]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setItems(res.data || []);
    } catch (e) {
      console.error('NotificationBell: failed to fetch notifications', e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) fetchItems();
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      console.error('NotificationBell: failed to mark notification read', e);
    }
  };

  const handleMarkAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      console.error('NotificationBell: failed to mark all notifications read', e);
    }
  };

  const handleClearAll = async () => {
    if (!items.length) return;
    // No confirm() — the "Notifications" panel is quick-use; the user can always
    // reach past reflections from the "See past reflections" link right below.
    try {
      await axios.delete(`${API}/notifications/clear-all`);
      setItems([]);
      setUnread(0);
    } catch (e) {
      console.error('NotificationBell: failed to clear notifications', e);
    }
  };

  const handleClickItem = (n) => {
    if (!n.read) handleMarkRead(n.id);
    // "Content" notifications (daily reflection, or anything without a meaningful
    // navigation target) open a read modal so the user actually sees the content.
    const isReadInPlace = n.type === 'daily_reflection' || !n.link || n.link === '/dashboard';
    if (isReadInPlace) {
      setReaderNotif(n);
      setOpen(false);
      return;
    }
    if (onNavigate) {
      setOpen(false);
      onNavigate(n.link);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 sm:w-96 p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b gap-2 flex-wrap">
          <p className="font-semibold text-slate-800">Notifications</p>
          <div className="flex items-center gap-3">
            {items.some((n) => !n.read) && (
              <button
                onClick={handleMarkAll}
                className="text-xs text-[#2B6B7F] hover:underline"
                data-testid="mark-all-read-button"
              >
                Mark all read
              </button>
            )}
            {items.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:underline flex items-center gap-1"
                data-testid="clear-all-notifications-button"
                title="Delete every notification"
              >
                <Trash2 className="w-3 h-3" />
                Clear all
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-center text-sm text-slate-500 py-6">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No notifications yet</p>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClickItem(n)}
                className={`w-full text-left px-3 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
                  n.read ? 'bg-white' : 'bg-blue-50/60'
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.read ? 'text-slate-700' : 'text-slate-900 font-semibold'}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">{fmt(n.created_at)}</p>
                  </div>
                  {n.read && <Check className="w-3 h-3 text-slate-300 mt-1.5 flex-shrink-0" />}
                </div>
              </button>
            ))
          )}
        </div>
        <button
          onClick={() => {
            setOpen(false);
            if (onNavigate) onNavigate('/reflections');
            else window.location.href = '/reflections';
          }}
          className="w-full text-center px-3 py-2.5 border-t bg-amber-50/50 hover:bg-amber-100/70 text-sm text-amber-800 flex items-center justify-center gap-1.5 transition-colors"
          data-testid="reflections-archive-link"
        >
          <BookOpen className="w-4 h-4" />
          See past Daily Reflections
        </button>
      </DropdownMenuContent>

      {/* Read-in-place modal for content notifications (daily reflection, etc.) */}
      <Dialog
        open={!!readerNotif}
        onOpenChange={(v) => {
          if (!v) {
            setReaderNotif(null);
            setBioOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="notification-reader-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2B6B7F] pr-6">
              {readerNotif?.title}
            </DialogTitle>
            {readerNotif?.created_at && (
              <DialogDescription className="text-xs">
                {fmt(readerNotif.created_at)}
              </DialogDescription>
            )}
          </DialogHeader>
          {readerNotif?.body && (
            <div className="py-2">
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {readerNotif.body}
              </p>
            </div>
          )}

          {/* Author bio — only for daily reflections. Collapsed by default,
              expandable so the reflection itself stays the focal point. */}
          {readerNotif?.type === 'daily_reflection' && (() => {
            const author = detectAuthor(readerNotif?.body);
            if (!author) return null;
            return (
              <div className="mt-2 border-t border-slate-200 pt-3" data-testid={`author-bio-${author.key}`}>
                <button
                  type="button"
                  onClick={() => setBioOpen((v) => !v)}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-[#2B6B7F] hover:text-[#245968] transition-colors"
                  data-testid="author-bio-toggle"
                >
                  <span>
                    Who was <strong>{author.name}</strong>?
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${bioOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {bioOpen && (
                  <div className="mt-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700 leading-relaxed">
                    <p className="text-xs text-slate-500 mb-1">
                      {author.dates} · {author.role}
                    </p>
                    <p className="whitespace-pre-line">{author.bio}</p>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setReaderNotif(null); setBioOpen(false); }}
              data-testid="notification-reader-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  );
};

export default NotificationBell;
